import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StarIcon from "@mui/icons-material/Star";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Paper,
    Typography,
} from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import React from "react";
import { calculateProgress } from "../../common/utils/PuzzlePlayLogic";

/**
 * Hidden-message / word-search progress panels (parity accordions).
 * `placement`: `sidebar` = under Words to Find; `underGrid` = fallback when words drawer hidden.
 */
function GridPlayProgressAccordions({
    placement,
    puzzleType,
    t,
    hiddenMessageLength,
    hiddenMessage,
    hiddenMessageProgress,
    pdfSolvedCurrent,
    pdfWordsTotal,
    puzzleWordsCount,
    isWordSearchComplete,
    completionActionsDisabled,
    settingsLoaded,
    isAuthenticated,
    setFeedbackDialogOpen,
    handleBackClick,
    handleSignUpClick,
}) {
    const outerSx =
        placement === "sidebar"
            ? { width: "100%", flexShrink: 0 }
            : { mt: 1, width: "100%" };

    const playAccent = "#3BA666";
    const playPaperBg = "#e8f5e8";

    if (puzzleType === "HIDDEN_MESSAGE") {
        return (
            <Box sx={outerSx}>
                <Accordion
                    defaultExpanded={false}
                    disableGutters
                    elevation={0}
                    sx={{
                        width: "100%",
                        maxWidth: "100%",
                        "&:before": { display: "none" },
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                        backgroundColor: "background.paper",
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: playAccent }} />}
                        sx={{
                            px: 2,
                            minHeight: 48,
                            color: playAccent,
                            "& .MuiAccordionSummary-content": {
                                my: 1,
                                alignItems: "center",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                gap: 2,
                                flexWrap: "wrap",
                                width: "100%",
                                color: playAccent,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, letterSpacing: "0.5px", color: "inherit" }}
                            >
                                {t("puzzle.playGrid.hidden-message")}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    color: "inherit",
                                }}
                            >
                                <Box component="span" sx={{ fontWeight: 600, color: "inherit" }}>
                                    ({hiddenMessageLength} {t("puzzle.playGrid.characters")})
                                </Box>
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 2 }}>
                        <Paper sx={{ p: 2, backgroundColor: playPaperBg, width: "100%", boxSizing: "border-box" }}>
                            {hiddenMessage ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", sm: "row" },
                                            justifyContent: "space-between",
                                            alignItems: { xs: "stretch", sm: "center" },
                                            gap: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.75,
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Typography variant="body1" sx={{ fontWeight: "bold", wordBreak: "break-word" }}>
                                                {hiddenMessage}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                flexShrink: 0,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                disabled={completionActionsDisabled}
                                                onClick={() => setFeedbackDialogOpen(true)}
                                                sx={{
                                                    px: 2,
                                                    py: 0.75,
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    fontSize: "0.875rem",
                                                }}
                                            >
                                                {t("puzzle.playGrid.feedback")}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={completionActionsDisabled}
                                                onClick={handleBackClick}
                                                sx={{
                                                    px: 2,
                                                    py: 0.75,
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    fontSize: "0.875rem",
                                                    backgroundColor: "#3BA666",
                                                    "&:hover": {
                                                        backgroundColor: "#2E8B57",
                                                    },
                                                }}
                                            >
                                                {t("puzzle.playGrid.complete")}
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Alert
                                        severity="info"
                                        variant="outlined"
                                        icon={<InfoOutlinedIcon fontSize="small" />}
                                        sx={{ py: 0.75, fontSize: "0.8125rem", alignItems: "center" }}
                                    >
                                        {t(
                                            "puzzle.playGrid.browser-refresh-hint",
                                            "The browser refresh button can reset your play progress. Avoid refreshing if you want to keep your current game."
                                        )}
                                    </Alert>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {t("puzzle.playGrid.find-all-to-reveal")}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={hiddenMessageProgress}
                                            sx={{ height: 4, borderRadius: 2, flex: 1 }}
                                        />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ minWidth: "fit-content", fontWeight: 500 }}
                                        >
                                            {pdfSolvedCurrent} / {pdfWordsTotal}
                                        </Typography>
                                    </Box>
                                    {settingsLoaded && !isAuthenticated && (
                                        <Box sx={{ mt: 2, display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={handleSignUpClick}
                                                startIcon={<StarIcon sx={{ fontSize: 18 }} />}
                                                sx={{
                                                    backgroundColor: "#ff9800",
                                                    "&:hover": { backgroundColor: "#e65100" },
                                                    px: 2,
                                                    py: 0.75,
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                }}
                                            >
                                                {t("core.signup.title")}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </AccordionDetails>
                </Accordion>
            </Box>
        );
    }

    if (puzzleType === "WORD_SEARCH") {
        return (
            <Box sx={outerSx}>
                <Accordion
                    defaultExpanded={false}
                    disableGutters
                    elevation={0}
                    sx={{
                        width: "100%",
                        maxWidth: "100%",
                        "&:before": { display: "none" },
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                        backgroundColor: "background.paper",
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: playAccent }} />}
                        sx={{
                            px: 2,
                            minHeight: 48,
                            color: playAccent,
                            "& .MuiAccordionSummary-content": {
                                my: 1,
                                alignItems: "center",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                gap: 2,
                                flexWrap: "wrap",
                                width: "100%",
                                color: playAccent,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, letterSpacing: "0.5px", color: "inherit" }}
                            >
                                {t("puzzle.playGrid.word-search")}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    color: "inherit",
                                }}
                            >
                                <Box component="span" sx={{ fontWeight: 600, color: "inherit" }}>
                                    ({puzzleWordsCount} {t("puzzle.playGrid.words")})
                                </Box>
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 2 }}>
                        <Paper sx={{ p: 2, backgroundColor: playPaperBg, width: "100%", boxSizing: "border-box" }}>
                            {isWordSearchComplete ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", sm: "row" },
                                            justifyContent: "space-between",
                                            alignItems: { xs: "stretch", sm: "center" },
                                            gap: 2,
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ fontWeight: "bold", flex: 1, wordBreak: "break-word" }}>
                                            {t("puzzle.playCross.all-found")}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                flexShrink: 0,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                disabled={completionActionsDisabled}
                                                onClick={() => setFeedbackDialogOpen(true)}
                                                sx={{
                                                    px: 2,
                                                    py: 0.75,
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    fontSize: "0.875rem",
                                                }}
                                            >
                                                {t("puzzle.playGrid.feedback")}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={completionActionsDisabled}
                                                onClick={handleBackClick}
                                                sx={{
                                                    px: 2,
                                                    py: 0.75,
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    fontSize: "0.875rem",
                                                    backgroundColor: "#3BA666",
                                                    "&:hover": {
                                                        backgroundColor: "#2E8B57",
                                                    },
                                                }}
                                            >
                                                {t("puzzle.playGrid.complete")}
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Alert
                                        severity="info"
                                        variant="outlined"
                                        icon={<InfoOutlinedIcon fontSize="small" />}
                                        sx={{ py: 0.75, fontSize: "0.8125rem", alignItems: "center" }}
                                    >
                                        {t(
                                            "puzzle.playGrid.browser-refresh-hint",
                                            "The browser refresh button can reset your play progress. Avoid refreshing if you want to keep your current game."
                                        )}
                                    </Alert>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {t("puzzle.playGrid.find-all-words")}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                pdfWordsTotal ? calculateProgress(pdfSolvedCurrent, pdfWordsTotal) : 0
                                            }
                                            sx={{ height: 4, borderRadius: 2, flex: 1 }}
                                        />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ minWidth: "fit-content", fontWeight: 500 }}
                                        >
                                            {pdfSolvedCurrent} / {pdfWordsTotal}
                                        </Typography>
                                    </Box>
                                    {settingsLoaded && !isAuthenticated && (
                                        <Box sx={{ mt: 2, display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={handleSignUpClick}
                                                startIcon={<StarIcon sx={{ fontSize: 18 }} />}
                                                sx={{
                                                    backgroundColor: "#ff9800",
                                                    "&:hover": { backgroundColor: "#e65100" },
                                                    px: 2,
                                                    py: 0.75,
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                }}
                                            >
                                                {t("core.signup.title")}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </AccordionDetails>
                </Accordion>
            </Box>
        );
    }

    return null;
}

export default GridPlayProgressAccordions;
